import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import Grid from '@material-ui/core/Grid';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';

import {validateCode} from 'api';
import {createRemarkingDocument, fetchRemarkingDictionary} from 'api/documents';
import {ReactComponent as ScanIcon} from 'assets/svg/scan.svg';
import {ReactComponent as TrashIcon} from 'assets/svg/trash.svg';
import {BackTo} from 'components/common/BackTo';
import {Button} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {IconButton} from 'components/ui-kit/IconButton';
import {Input} from 'components/ui-kit/Input';
import {ContentContainer, StatusWrapper} from 'components/ui-kit/PageComponents';
import {QRScannerModal} from 'components/ui-kit/ScanCode';
import {Select, SelectOption} from 'components/ui-kit/Select';
import {PRODUCT_STATUS_COLORS} from 'core/constants/statuses';
import {DocumentReportType} from 'core/dictionaries';
import {RestError, showRestError} from 'core/errors';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {colors} from 'theme';
import {RemarkingDictionaryResponse, RemarkingReason, RemarkingReportClientRequest} from 'types';
import {CodeResponseType} from 'types/validation';
import {formatDate} from 'utils/date';
import {hasOnlyLatinLetters} from 'utils/string';

import {
    Actions,
    Code,
    CodeCard,
    CodeCardHeader,
    CodeControls,
    CodeDetails,
    Detail,
    DetailLabel,
    DetailValue,
    SectionTitle,
    Title,
} from './styled';
import {remarkingSchema} from './validation';

interface FormData {
    markingCode: string;
    remarkingReason: RemarkingReason | null;
    comment: string;
}

const EMPTY_DICTIONARY: RemarkingDictionaryResponse = {remarkingReasons: []};

export const CreateRemarking = () => {
    const {t} = useTranslation();
    const [dictionary, setDictionary] = useState(EMPTY_DICTIONARY);
    const [codeInfo, setCodeInfo] = useState<CodeResponseType['info'] | null>(null);
    const [scanOpen, setScanOpen] = useState(false);
    const [dictionaryPending, setDictionaryPending] = useState(true);
    const [validationPending, setValidationPending] = useState(false);
    const [submitPending, setSubmitPending] = useState(false);

    const {
        control,
        handleSubmit,
        setValue,
        formState: {isValid},
    } = useForm<FormData>({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(remarkingSchema),
        defaultValues: {
            markingCode: '',
            remarkingReason: null,
            comment: '',
        },
    });
    useEffect(() => {
        let mounted = true;

        fetchRemarkingDictionary()
            .then((data) => {
                if (mounted) setDictionary(data);
            })
            .catch((error) => showRestError(error as RestError))
            .finally(() => {
                if (mounted) setDictionaryPending(false);
            });

        return () => {
            mounted = false;
        };
    }, []);

    const reasonOptions = useMemo<SelectOption<RemarkingReason>[]>(
        () =>
            dictionary.remarkingReasons.map((reason) => ({
                value: reason,
                label: t(`dictionaries.documents.remarking.reasons.${reason}`),
            })),
        [dictionary.remarkingReasons, t],
    );

    const clearCode = useCallback(() => {
        setCodeInfo(null);
        setValue('markingCode', '', {shouldValidate: true});
    }, [setValue]);

    const onEnterCode = useCallback(
        async (markingCode: string) => {
            if (!markingCode || validationPending) return;

            if (!hasOnlyLatinLetters(markingCode)) {
                toast.warning(t('components.scan.layoutWarn'));
                return;
            }

            setValidationPending(true);
            try {
                const {data} = await validateCode({
                    type: DocumentReportType.REMARKING,
                    markingCode,
                });

                if (data.errors.length) {
                    setCodeInfo(null);
                    setValue('markingCode', '', {shouldValidate: true});
                    data.errors.forEach(({message}) => toast.error(message));
                    return;
                }

                setCodeInfo(data.info);
                setValue('markingCode', data.info.identityCode, {shouldValidate: true});
                setScanOpen(false);
            } catch (error) {
                setCodeInfo(null);
                setValue('markingCode', '', {shouldValidate: true});
                showRestError(error as RestError);
            } finally {
                setValidationPending(false);
            }
        },
        [setValue, t, validationPending],
    );

    const onSubmit = async ({markingCode, remarkingReason, comment}: FormData) => {
        if (!remarkingReason || !codeInfo) return;

        const payload: RemarkingReportClientRequest = {
            markingCode,
            remarkingReason,
            ...(comment.trim() && {comment: comment.trim()}),
        };

        setSubmitPending(true);
        try {
            const {reportId} = await createRemarkingDocument(payload);
            toast.success(t('pages.documents.form.documentCreated', {number: reportId}), {
                className: 'Toastify__toast__wide',
            });
            history.push(ROUTE.DOCUMENTS_DOCUMENTS);
        } catch (error) {
            showRestError(error as RestError);
        } finally {
            setSubmitPending(false);
        }
    };

    const detailValue = (label: string, value?: React.ReactNode) => (
        <Detail>
            <DetailLabel>{label}</DetailLabel>
            <DetailValue>{value || '—'}</DetailValue>
        </Detail>
    );

    return (
        <>
            <BackTo to={ROUTE.DOCUMENTS_DOCUMENTS}>{t('pages.documents.back')}</BackTo>
            <ContentContainer>
                <Title>{t('pages.documents.remarking.title')}</Title>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <SectionTitle>{t('pages.documents.remarking.generalInfo')}</SectionTitle>
                    <Grid container spacing={3}>
                        <Grid item md={6} xs={12}>
                            <Controller
                                control={control}
                                name="remarkingReason"
                                render={(props, {invalid}) => (
                                    <Select
                                        isRequired
                                        label={t('pages.documents.remarking.reason')}
                                        placeholder={t(
                                            'pages.documents.remarking.reasonPlaceholder',
                                        )}
                                        options={reasonOptions}
                                        value={
                                            reasonOptions.find(
                                                (option) => option.value === props.value,
                                            ) ?? null
                                        }
                                        hasError={invalid}
                                        isLoading={dictionaryPending}
                                        onChange={(option: SelectOption<RemarkingReason>) =>
                                            props.onChange(option?.value ?? null)
                                        }
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item md={6} xs={12}>
                            <Controller
                                control={control}
                                name="comment"
                                render={(props, {invalid}) => (
                                    <Input
                                        {...props}
                                        label={t('pages.documents.remarking.comment')}
                                        placeholder={t(
                                            'pages.documents.remarking.commentPlaceholder',
                                        )}
                                        value={props.value}
                                        hasError={invalid}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>

                    <SectionTitle>{t('pages.documents.remarking.markingCode')}</SectionTitle>
                    <Controller
                        control={control}
                        name="markingCode"
                        render={(props, {invalid}) =>
                            codeInfo ? null : (
                                <CodeControls>
                                    <Input
                                        isRequired
                                        label={t('pages.documents.remarking.markingCode')}
                                        placeholder={t(
                                            'pages.documents.remarking.markingCodePlaceholder',
                                        )}
                                        value={props.value}
                                        hasError={invalid}
                                        onChange={(event) => onEnterCode(event.target.value)}
                                    />
                                    <Button
                                        type="button"
                                        disabled={validationPending}
                                        onClick={() => setScanOpen(true)}
                                    >
                                        {t('components.scan.button')}
                                        <Icon
                                            source={ScanIcon}
                                            width={20}
                                            height={20}
                                            style={{marginLeft: 6}}
                                        />
                                    </Button>
                                </CodeControls>
                            )
                        }
                    />

                    {codeInfo && (
                        <CodeCard>
                            <CodeCardHeader>
                                <Code>{codeInfo.identityCode}</Code>
                                <IconButton
                                    type="button"
                                    onClick={clearCode}
                                    icon={
                                        <Icon
                                            source={TrashIcon}
                                            width={16}
                                            height={16}
                                            fillColor={colors.danger}
                                        />
                                    }
                                />
                            </CodeCardHeader>
                            <CodeDetails>
                                {detailValue(
                                    t('pages.documents.remarking.productName'),
                                    codeInfo.productName,
                                )}
                                {detailValue(
                                    t('pages.documents.remarking.productCode'),
                                    codeInfo.productCode || codeInfo.gtin,
                                )}
                                {detailValue(
                                    t('pages.documents.remarking.productGroup'),
                                    codeInfo.productGroupCode
                                        ? t(
                                              `dictionaries.productGroups.${codeInfo.productGroupCode}`,
                                          )
                                        : undefined,
                                )}
                                {detailValue(
                                    t('pages.documents.remarking.packagingType'),
                                    codeInfo.packagingType
                                        ? t(
                                              `dictionaries.products.packageType.${codeInfo.packagingType}`,
                                          )
                                        : undefined,
                                )}
                                {detailValue(
                                    t('pages.documents.remarking.status'),
                                    codeInfo.status ? (
                                        <StatusWrapper
                                            color={
                                                PRODUCT_STATUS_COLORS[codeInfo.status ?? 'DEFAULT']
                                            }
                                        >
                                            {t(`dictionaries.products.status.${codeInfo.status}`)}
                                        </StatusWrapper>
                                    ) : (
                                        undefined
                                    ),
                                )}
                                {detailValue(
                                    t('pages.documents.remarking.storageLocation'),
                                    codeInfo.nameStorageLocation,
                                )}
                                {detailValue(
                                    t('pages.documents.remarking.manufacturingDate'),
                                    codeInfo.manufacturingDate
                                        ? formatDate(codeInfo.manufacturingDate)
                                        : undefined,
                                )}
                                {detailValue(
                                    t('pages.documents.remarking.expirationDate'),
                                    codeInfo.expirationDate
                                        ? formatDate(codeInfo.expirationDate)
                                        : undefined,
                                )}
                                {detailValue(
                                    t('pages.documents.remarking.batchNumber'),
                                    codeInfo.batchNumber,
                                )}
                            </CodeDetails>
                        </CodeCard>
                    )}

                    <Actions>
                        <Button
                            type="submit"
                            style={{width: 'auto'}}
                            disabled={
                                submitPending ||
                                validationPending ||
                                dictionaryPending ||
                                !codeInfo ||
                                !isValid
                            }
                        >
                            {t('pages.documents.remarking.submit')}
                        </Button>
                    </Actions>
                </form>
            </ContentContainer>
            <QRScannerModal
                open={scanOpen}
                pending={validationPending}
                onClose={() => setScanOpen(false)}
                onScan={onEnterCode}
                onManual={onEnterCode}
            />
        </>
    );
};
